import { APP_ERROR_CODE } from './JsonRpcError';
import { truncate } from './logString';
import { CAPTURE_IMAGE_MAX_BASE64_LENGTH } from '../../ViewImageCapture/CaptureImageResult';
import {
  CaptureBusyError,
  CaptureNotReadyError,
  CapturePayloadTooLargeError,
  CapturePermissionDeniedError,
  CaptureViewNameAmbiguousError,
  CaptureViewNotFoundError,
} from '../../ViewImageCapture/ViewImageCaptureError';
import type { ApplicationError } from './JsonRpcError';
import type { CaptureTargetView } from '../../ViewImageCapture/CaptureViewTarget';

// ---- キャプチャ実装（useViewImageCapture）が throw する型付きエラーを JSON-RPC の失敗へ変換する ----

export type ExecuteCaptureFailure =
  | { ok: false; kind: 'not_ready' }
  | { ok: false; kind: 'permission_denied' }
  | { ok: false; kind: 'busy' }
  | { ok: false; kind: 'view_not_found'; specified: string; views: CaptureTargetView[] }
  | { ok: false; kind: 'view_name_ambiguous'; viewName: string; candidates: CaptureTargetView[] }
  | { ok: false; kind: 'payload_too_large'; base64Length: number }
  | { ok: false; kind: 'capture_failed'; detail: string };

export function captureFailureFromError(e: unknown): ExecuteCaptureFailure {
  if (e instanceof CaptureNotReadyError) {
    return { ok: false, kind: 'not_ready' };
  }
  if (e instanceof CapturePermissionDeniedError) {
    return { ok: false, kind: 'permission_denied' };
  }
  if (e instanceof CaptureBusyError) {
    return { ok: false, kind: 'busy' };
  }
  if (e instanceof CaptureViewNotFoundError) {
    return { ok: false, kind: 'view_not_found', specified: e.specified, views: e.views };
  }
  if (e instanceof CaptureViewNameAmbiguousError) {
    return { ok: false, kind: 'view_name_ambiguous', viewName: e.viewName, candidates: e.candidates };
  }
  if (e instanceof CapturePayloadTooLargeError) {
    return { ok: false, kind: 'payload_too_large', base64Length: e.base64Length };
  }
  const detail = e instanceof Error ? e.message : String(e);
  return { ok: false, kind: 'capture_failed', detail };
}

export function toCaptureError(failure: ExecuteCaptureFailure): ApplicationError {
  switch (failure.kind) {
    case 'not_ready':
      return captureNotReadyError();
    case 'permission_denied':
      return imageExportNotPermittedError();
    case 'busy':
      return captureBusyError();
    case 'view_not_found':
      return viewNotFoundError(failure.specified, failure.views);
    case 'view_name_ambiguous':
      return viewNameAmbiguousError(failure.viewName, failure.candidates);
    case 'payload_too_large':
      return capturePayloadTooLargeError(failure.base64Length);
    case 'capture_failed':
      return captureFailedError(failure.detail);
    default: {
      const _exhaustive: never = failure;
      throw new Error(`Unhandled ExecuteCaptureFailure: ${JSON.stringify(_exhaustive)}`);
    }
  }
}

export function captureNotReadyError(): ApplicationError {
  return {
    kind: 'application',
    code: APP_ERROR_CODE.TARGET_NOT_READY,
    message: 'Target not ready',
    data: {
      reason: 'bridge_not_ready',
      detail: 'The image capture handler is not ready in the current browser page.',
      recovery: 'open_target_and_retry',
      guidance:
        'Open the target ViewModel page in the browser, wait until it finishes loading, and then run the request again.',
    },
  };
}

export function viewNotFoundError(specified: string, views: CaptureTargetView[]): ApplicationError {
  return {
    kind: 'application',
    code: APP_ERROR_CODE.TARGET_NOT_FOUND,
    message: 'Target not found',
    data: {
      reason: 'view_not_found',
      // 現存ビュー一覧の JSON はエージェントの自己回復（正しい viewId の再指定）に使うため、
      // truncate で途中を切って JSON を壊さないよう指定値側のみ truncate する
      detail: `view not found: ${truncate(specified)}. existing views: ${JSON.stringify(views)}`,
      recovery: 'check_input',
      guidance: 'Specify one of the existing view ids included in the detail and send the request again.',
    },
  };
}

export function viewNameAmbiguousError(viewName: string, candidates: CaptureTargetView[]): ApplicationError {
  return {
    kind: 'application',
    code: APP_ERROR_CODE.TARGET_NOT_FOUND,
    message: 'Target not found',
    data: {
      reason: 'view_name_ambiguous',
      // 候補一覧の JSON はエージェントの自己回復（viewId での再指定）に使うため、
      // truncate で途中を切って JSON を壊さないよう viewName 側のみ truncate する
      detail: `view name is ambiguous: ${truncate(viewName)}. matched views: ${JSON.stringify(candidates)}`,
      recovery: 'check_input',
      guidance:
        'Multiple views have the specified name. Specify the target view by its id, choosing from the matched views included in the detail.',
    },
  };
}

export function imageExportNotPermittedError(): ApplicationError {
  return {
    kind: 'application',
    code: APP_ERROR_CODE.PERMISSION_DENIED,
    message: 'Permission denied',
    data: {
      reason: 'image_export_not_permitted',
      detail: 'Image export is not permitted by the current group license.',
      recovery: 'check_permission',
      guidance: 'Ask the group administrator to review the group license for image export, and then retry.',
    },
  };
}

export function captureBusyError(): ApplicationError {
  return {
    kind: 'application',
    code: APP_ERROR_CODE.OPERATION_REJECTED,
    message: 'Operation rejected',
    data: {
      reason: 'capture_busy',
      detail: 'Another image capture is currently in progress.',
      recovery: 'retry_later',
      guidance: 'Wait for the in-progress image capture to complete and then retry.',
    },
  };
}

export function captureFailedError(detail: string): ApplicationError {
  return {
    kind: 'application',
    code: APP_ERROR_CODE.METHOD_EXECUTION_FAILED,
    message: 'Method execution failed',
    data: {
      reason: 'capture_failed',
      detail: truncate(detail),
      recovery: 'unknown',
      guidance: 'Check the error detail and retry if appropriate',
    },
  };
}

export function capturePayloadTooLargeError(base64Length: number): ApplicationError {
  return {
    kind: 'application',
    code: APP_ERROR_CODE.METHOD_EXECUTION_FAILED,
    message: 'Method execution failed',
    data: {
      reason: 'payload_too_large',
      detail: `captured image is too large to send: base64 length ${base64Length} exceeds ${CAPTURE_IMAGE_MAX_BASE64_LENGTH}`,
      recovery: 'cannot_recover',
      guidance:
        'The captured image exceeds the transfer size limit. Reduce the view model contents or capture a single view instead.',
    },
  };
}
